import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Play, 
  Pause, 
  Square, 
  RefreshCw, 
  Search, 
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { UploadJob } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const UploadMonitor: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isRealTimeEnabled, setIsRealTimeEnabled] = useState(true);
  const queryClient = useQueryClient();

  const { data: uploadsData, isLoading } = useQuery({
    queryKey: ['uploads', currentPage, statusFilter, searchTerm],
    queryFn: () => adminApi.getUploads({
      page: currentPage,
      limit: 20,
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: searchTerm || undefined,
    }),
    refetchInterval: isRealTimeEnabled ? 2000 : false,
  });

  // Real-time updates via SSE
  useEffect(() => {
    if (!isRealTimeEnabled) return;

    const eventSource = adminApi.getUploadsSSE();
    
    eventSource.onmessage = (event) => {
      const upload: UploadJob = JSON.parse(event.data);
      
      // Update the query cache with new data
      queryClient.setQueryData(['uploads', currentPage, statusFilter, searchTerm], (oldData: any) => {
        if (!oldData) return oldData;
        
        const updatedItems = oldData.items.map((item: UploadJob) => 
          item.id === upload.id ? upload : item
        );
        
        // If it's a new upload, add it to the beginning
        if (!updatedItems.find((item: UploadJob) => item.id === upload.id)) {
          updatedItems.unshift(upload);
        }
        
        return {
          ...oldData,
          items: updatedItems.slice(0, 20), // Keep only first 20 items
        };
      });
    };

    eventSource.onerror = () => {
      console.error('SSE connection failed');
      setIsRealTimeEnabled(false);
      toast.error('Real-time updates disconnected');
    };

    return () => {
      eventSource.close();
    };
  }, [isRealTimeEnabled, queryClient, currentPage, statusFilter, searchTerm]);

  const handleRetry = async (uploadId: string) => {
    try {
      await adminApi.retryUpload(uploadId);
      toast.success('Upload retry initiated');
      queryClient.invalidateQueries({ queryKey: ['uploads'] });
    } catch (error) {
      toast.error('Failed to retry upload');
    }
  };

  const handleCancel = async (uploadId: string) => {
    try {
      await adminApi.cancelUpload(uploadId);
      toast.success('Upload cancelled');
      queryClient.invalidateQueries({ queryKey: ['uploads'] });
    } catch (error) {
      toast.error('Failed to cancel upload');
    }
  };

  const handleDelete = async (uploadId: string) => {
    if (!confirm('Are you sure you want to delete this upload?')) return;
    
    try {
      await adminApi.deleteUpload(uploadId);
      toast.success('Upload deleted');
      queryClient.invalidateQueries({ queryKey: ['uploads'] });
    } catch (error) {
      toast.error('Failed to delete upload');
    }
  };

  const statusOptions = [
    { value: 'all', label: 'All Status', count: uploadsData?.total || 0 },
    { value: 'pending', label: 'Pending', count: 0 },
    { value: 'processing', label: 'Processing', count: 0 },
    { value: 'completed', label: 'Completed', count: 0 },
    { value: 'failed', label: 'Failed', count: 0 },
    { value: 'cancelled', label: 'Cancelled', count: 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Upload Monitor
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Real-time monitoring of all upload jobs and their progress
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={() => setIsRealTimeEnabled(!isRealTimeEnabled)}
            className={clsx(
              'inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-twitch-500',
              isRealTimeEnabled
                ? 'bg-green-50 text-green-700 border-green-300'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            )}
          >
            {isRealTimeEnabled ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {isRealTimeEnabled ? 'Pause' : 'Resume'} Live Updates
          </button>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['uploads'] })}
            className="btn-outline"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search uploads by filename, user, or title..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="input pl-10"
              />
            </div>
          </div>
          
          {/* Status Filter */}
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Uploads Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <AnimatePresence>
                {uploadsData?.items.map((upload) => (
                  <UploadRow
                    key={upload.id}
                    upload={upload}
                    onRetry={handleRetry}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                  />
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-twitch-600" />
          </div>
        )}

        {!isLoading && (!uploadsData?.items || uploadsData.items.length === 0) && (
          <div className="text-center py-12">
            <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No uploads found</h3>
            <p className="text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Upload jobs will appear here as they are created'
              }
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {uploadsData && uploadsData.total > 20 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
          <div className="flex items-center">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(currentPage * 20, uploadsData.total)}
              </span>{' '}
              of <span className="font-medium">{uploadsData.total}</span> results
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!uploadsData.hasPrev}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!uploadsData.hasNext}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface UploadRowProps {
  upload: UploadJob;
  onRetry: (id: string) => void;
  onCancel: (id: string) => void;
  onDelete: (id: string) => void;
}

const UploadRow: React.FC<UploadRowProps> = ({ upload, onRetry, onCancel, onDelete }) => {
  const [showActions, setShowActions] = useState(false);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Clock className="h-5 w-5 text-yellow-500 animate-pulse" />;
      case 'failed':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDuration = (startedAt: string, completedAt?: string) => {
    const start = new Date(startedAt);
    const end = completedAt ? new Date(completedAt) : new Date();
    const diff = end.getTime() - start.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <motion.tr
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="hover:bg-gray-50"
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
              {upload.filename}
            </div>
            <div className="text-sm text-gray-500 max-w-xs truncate">
              {upload.metadata.streamTitle || 'No title'}
            </div>
          </div>
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {getStatusIcon(upload.status)}
          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(upload.status)}`}>
            {upload.status}
          </span>
        </div>
        {upload.errorMessage && (
          <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={upload.errorMessage}>
            {upload.errorMessage}
          </div>
        )}
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
            <div
              className={clsx(
                'h-2 rounded-full transition-all duration-300',
                upload.status === 'completed' ? 'bg-green-500' :
                upload.status === 'failed' ? 'bg-red-500' :
                upload.status === 'processing' ? 'bg-yellow-500' : 'bg-gray-400'
              )}
              style={{ width: `${upload.progress}%` }}
            />
          </div>
          <span className="text-sm text-gray-900">{upload.progress}%</span>
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <div>{formatBytes(upload.originalSize)}</div>
        {upload.processedSize && (
          <div className="text-xs text-gray-500">
            → {formatBytes(upload.processedSize)}
          </div>
        )}
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div>{new Date(upload.startedAt).toLocaleString()}</div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDuration(upload.startedAt, upload.completedAt)}
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="text-gray-400 hover:text-gray-500 p-1 rounded-full hover:bg-gray-100"
          >
            <MoreHorizontal className="h-5 w-5" />
          </button>
          
          {showActions && (
            <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
              <div className="py-1">
                {upload.status === 'failed' && (
                  <button
                    onClick={() => {
                      onRetry(upload.id);
                      setShowActions(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <RefreshCw className="h-4 w-4 mr-3" />
                    Retry
                  </button>
                )}
                
                {(upload.status === 'processing' || upload.status === 'pending') && (
                  <button
                    onClick={() => {
                      onCancel(upload.id);
                      setShowActions(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Square className="h-4 w-4 mr-3" />
                    Cancel
                  </button>
                )}
                
                <button
                  onClick={() => {
                    onDelete(upload.id);
                    setShowActions(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                >
                  <XCircle className="h-4 w-4 mr-3" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </td>
    </motion.tr>
  );
};

export default UploadMonitor;